import { z } from 'zod';

import { datetimeSchema } from '../../common';
import { buildQueryString } from '../../utils/querystring';
import { makeEndpoint } from '../common';

/**
 * Duplicated from api/src/saved-filter/shared.ts for frontend use.
 * Keep in sync with API schemas.
 */
export enum SavedFilterType {
  Bookings = 'bookings',
  Sessions = 'sessions',
  Reports = 'reports',
  Experiences = 'experiences',
  Resources = 'resources',
}

export const SessionsFiltersSchema = z
  .object({
    experienceIds: z.array(z.string()).optional(),
    resourceGroupCollectionIds: z.array(z.string()).optional(),
    hostIds: z.array(z.string()).optional(),
    locationIds: z.array(z.string()).optional(),
    categoryIds: z.array(z.string()).optional(),
    products: z.array(z.string()).optional(),
  })
  .strict();

export const SavedFilterSchema = z.object({
  id: z.string(),
  brandId: z.string(),
  name: z.string(),
  createdById: z.string(),
  type: z.nativeEnum(SavedFilterType),
  options: z.record(z.string(), z.unknown()),
  createdAt: datetimeSchema,
  updatedAt: datetimeSchema,
  deletedAt: datetimeSchema.nullable(),
});

export type SavedFilter = z.infer<typeof SavedFilterSchema>;

export const SavedFilterCreateSchema = z.discriminatedUnion('type', [
  z
    .object({
      name: z.string().min(1),
      type: z.literal(SavedFilterType.Bookings),
      options: SessionsFiltersSchema.optional().default({}),
    })
    .strict(),
  z
    .object({
      name: z.string().min(1),
      type: z.enum([
        SavedFilterType.Sessions,
        SavedFilterType.Reports,
        SavedFilterType.Experiences,
        SavedFilterType.Resources,
      ]),
      options: z.record(z.string(), z.unknown()).optional().default({}),
    })
    .strict(),
]);

export type SavedFilterCreate = z.input<typeof SavedFilterCreateSchema>;

export const SavedFilterUpdateSchema = z
  .object({
    name: z.string().min(1).optional(),
    type: z.nativeEnum(SavedFilterType).optional(),
    options: z.record(z.string(), z.unknown()).optional(),
  })
  .strict();

export const SavedFilterUpdateBaseSchema = z
  .object({
    name: z.string().min(1).optional(),
    type: z.nativeEnum(SavedFilterType).optional(),
  })
  .strict();

export type SavedFilterUpdate = z.input<typeof SavedFilterUpdateSchema>;

export type SavedFilterUpdateBase = z.input<typeof SavedFilterUpdateBaseSchema>;

const SavedFilterPageMetaSchema = z.object({
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1),
});

const SavedFilterPageSchema = z.object({
  items: z.array(SavedFilterSchema),
  meta: SavedFilterPageMetaSchema,
});

const listSavedFiltersQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(10),
  sortBy: z.enum(['name', 'createdAt']).default('createdAt'),
  sortDir: z.enum(['asc', 'desc']).default('desc'),
  q: z.string().optional(),
  includeDeleted: z.union([z.boolean(), z.enum(['true', 'false'])]).optional(),
});

export type ListSavedFiltersQuery = z.infer<typeof listSavedFiltersQuerySchema>;

export const CreateSavedFilterEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ brandId }: { brandId: string }) => `v1/brands/${brandId}/saved-filters`,
  responseSchema: SavedFilterSchema,
  requestSchema: SavedFilterCreateSchema,
});

export const ListSavedFiltersEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ brandId, query }: { brandId: string; query: z.input<typeof listSavedFiltersQuerySchema> }) =>
    `v1/brands/${brandId}/saved-filters?${buildQueryString(query)}`,
  responseSchema: SavedFilterPageSchema,
  requestSchema: z.void(),
  querySchema: listSavedFiltersQuerySchema,
});

export const GetSavedFilterEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ brandId, filterId }: { brandId: string; filterId: string }) => `v1/brands/${brandId}/saved-filters/${filterId}`,
  responseSchema: SavedFilterSchema,
  requestSchema: z.void(),
});

export const UpdateSavedFilterEndpoint = makeEndpoint({
  method: 'PATCH',
  url: ({ brandId, filterId }: { brandId: string; filterId: string }) => `v1/brands/${brandId}/saved-filters/${filterId}`,
  responseSchema: SavedFilterSchema,
  requestSchema: SavedFilterUpdateSchema,
});

export const DeleteSavedFilterEndpoint = makeEndpoint({
  method: 'DELETE',
  url: ({ brandId, filterId }: { brandId: string; filterId: string }) => `v1/brands/${brandId}/saved-filters/${filterId}`,
  responseSchema: z.void(),
  requestSchema: z.void(),
});
